import React, { useState, useEffect, useRef } from 'react';
import { 
  Form, 
  Button, 
  Space, 
  message,
  Spin,
  Alert
} from 'antd';
import moment from 'moment';
import HeartLoader from '../../../components/shared/HeartLoader';
import { 
  SaveOutlined, 
  CheckCircleOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStethoscope,
  faChartLine,
  faClockRotateLeft,
  faUser,
  faCalendarDays,
  faCommentDots,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import PatientService from '../../../services/PatientService';
import StationService from '../../../services/StationService';
import VisitService from '../../../services/VisitService';
import { usePatientForm } from './hooks/usePatientForm';
import PatientInfoTab from './components/PatientInfoTab';
import MedicalTestingTab from './components/MedicalTestingTab';
import { EGFR_RANGE_MESSAGE, isEgfrOutOfRange } from './utils/constants';
// MultipleReadingsTab now embedded inside MedicalTestingTab
import TrendsTab from './components/TrendsTab';
import VisitHistoryTab from './components/VisitHistoryTab';
import EventChangeTab from './components/EventChangeTab';
import PatientConversation from '../../../components/PatientConversation';
import PatientEmailPanel from '../../../components/PatientEmailPanel';
import { useStore } from '../../../hooks/storeContext';
import { 
  Container, 
  HeaderContainer, 
  FormCard,
  SaveButton,
  HeaderWrapper,
  TitleSection,
  PatientNameRow,
  PatientName,
  NameDivider,
  PatientInlineId,
  StatusBadge,
  RiskScoreBadge,
  LoadingContainer,
  EditTabsContainer,
  EditTabButton,
  FormFooter
} from './styled';

const PatientEditView = ({ 
  patient, 
  onBack,
  onSuccess,
  showCompleteButton = false,
  editVisitId = null,
  readOnly = false,
  suppressHeader = false,
  forcedActiveTab, // externally control active tab key
  onActiveTabChange, // callback when user changes tab internally
  hideTabs = false, // hide top tab bar (side menu controls navigation)
  recordsMode = false, // member records edit context (enables save gating)
  initialPatientData = null // pre-fetched patient data to avoid duplicate API call
}) => {
  const [form] = Form.useForm();
  const { authStore } = useStore();
  const canMessage = authStore.isAdmin || authStore.hasPermission('messaging');
  const canEmail = authStore.isAdmin || authStore.hasPermission('case_management');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false); // Separate loading state for completing
  const [loading, setLoading] = useState(false);
  const [fullPatientData, setFullPatientData] = useState(null);
  const [internalActiveTab, setInternalActiveTab] = useState('1');
  const activeTab = forcedActiveTab || internalActiveTab;
  const [isViewingHistoricalVisit, setIsViewingHistoricalVisit] = useState(false);
  // Stable patient ID for useEffect dependency (avoids re-runs from inline prop objects)
  const stablePatientId = patient?.patient?.patientId || patient?.patientId || patient?._id;
  const visitBaselineRef = useRef(null); // stores JSON snapshot of current visit data for dirty comparison
  const [isVisitDirty, setIsVisitDirty] = useState(false);
  const [isUnsavedNewVisit, setIsUnsavedNewVisit] = useState(false);
  const visitBaselineFieldsRef = useRef(null); // baseline of visit-scoped form fields (labs, ekg, measurements, notes)
  // Stable current visit ID reference (prevents regeneration each render)
  const currentVisitIdRef = useRef(null);
  // Track whether a brand new BP reading has been added (unsaved) in records mode
  const [hasUnsavedNewReading, setHasUnsavedNewReading] = useState(false);
  // Baseline reading IDs for current visit (to detect truly new readings)
  const baselineReadingIdsRef = useRef(new Set());
  // Track if component is initializing to prevent dirty state during load
  const isInitializingRef = useRef(true);
  const autoSavingRef = useRef(false);

  const collectCurrentVisitReadingIds = (medicalReadingsArr, visitId) => {
    const visit = (medicalReadingsArr || []).find(v => v.visitId === visitId);
    if (!visit) return [];
    return (visit.vitalSigns || []).map((r, idx) => r.id || r._id || `${visitId}_${r.timestamp || idx}_${idx}`);
  };

  const establishBaselineIfNeeded = (medicalReadingsArr, visitId) => {
    if (!recordsMode) return;
    if (!visitId) return;
    // Always refresh baseline when full patient data changes or after save
    const ids = collectCurrentVisitReadingIds(medicalReadingsArr, visitId);
    baselineReadingIdsRef.current = new Set(ids);
    setHasUnsavedNewReading(false);
  };

  // evaluateUnsavedFromIds removed: BP readings now embedded and save gating handled elsewhere

  // Use custom hook for form logic
  usePatientForm(form);

  // Fetch complete patient information when component loads
  useEffect(() => {
    const fetchFullPatientData = async () => {
      if (!patient) return;
      setLoading(true);
      // Reset initialization flag when fetching new patient data
      isInitializingRef.current = true;
      try {
        // Use pre-fetched data if available, otherwise make the API call
        let fullData;
        if (initialPatientData) {
          fullData = initialPatientData;
        } else {
          const actualPatientId = patient.patient?.patientId || patient.patientId || patient._id;
          const response = await PatientService.getPatientDetails(actualPatientId);
          fullData = response.data;
        }
        setFullPatientData(fullData);
        const medicalReadings = fullData.screeningData?.medicalReadings || [];

        // Determine / set stable current visit id if not editing existing
        if (recordsMode && editVisitId) {
          // Always treat the externally selected visit as the working visit
          currentVisitIdRef.current = editVisitId;
        } else if (editVisitId) {
          currentVisitIdRef.current = editVisitId;
        } else if (!currentVisitIdRef.current) {
          // Always use the queue visit's ID - this ensures data saved earlier today is found
          // The queue visit (patient.visitId) is the source of truth for the current screening
          currentVisitIdRef.current = patient?.visitId || patient?._id;
        }

        const activeVisit = editVisitId
          ? medicalReadings.find(v => v.visitId === editVisitId)
          : medicalReadings.find(v => v.visitId === currentVisitIdRef.current);

        let latestVitalSign, latestBloodTest, latestEkgTest, bodyMeasurements;
        if (activeVisit) {
          latestVitalSign = activeVisit.vitalSigns?.[activeVisit.vitalSigns.length - 1];
          latestBloodTest = activeVisit.bloodTests?.[activeVisit.bloodTests.length - 1];
          latestEkgTest = activeVisit.ekgTests?.[activeVisit.ekgTests.length - 1];
          bodyMeasurements = activeVisit.bodyMeasurements || {};
        } else {
          bodyMeasurements = {};
        }

        const formValues = {
          // Core demographics / personal info
          firstName: fullData.firstName,
          lastName: fullData.lastName,
            emailAddress: fullData.emailAddress,
            phoneNumber: fullData.phoneNumber,
            dateOfBirth: fullData.dateOfBirth ? moment(fullData.dateOfBirth) : null,
            birthGender: fullData.birthGender,
            raceEthnicity: fullData.raceEthnicity,
            raceEthnicitySubgroup: fullData.raceEthnicitySubgroup,
            householdIncome: fullData.householdIncome,
            householdSize: fullData.householdSize,
            insuranceCoverage: fullData.insuranceCoverage,
            stateOfResidence: fullData.stateOfResidence,
            eventZip: fullData.eventZip,
            // Member info custom fields (previously missing)
            heartAttackOrStroke: fullData.heartAttackOrStroke || [],
            stressLevel: fullData.stressLevel || '',
            mentalHealthInterest: fullData.mentalHealthInterest || '',
            medicalConditions: fullData.medicalConditions || [],
            referredToCardiologist: fullData.referredToCardiologist || '',
            isParentOrCaregiver: fullData.isParentOrCaregiver || '',
            currentMedications: fullData.currentMedications || [],
            physicalActivityFrequency: fullData.physicalActivityFrequency || '',
            resourceAccess: fullData.resourceAccess || [],
            mediaRelease: fullData.mediaRelease || '',
            emergencyContactName: fullData.emergencyContactName || '',
            emergencyContactPhone: fullData.emergencyContactPhone || '',
            acknowledgeContraindications: fullData.acknowledgeContraindications || [],
            bhaWaiverConsent: !!fullData.bhaWaiverConsent,
            transactionalSmsConsent: !!fullData.transactionalSmsConsent,
            marketingSmsConsent: !!fullData.marketingSmsConsent,
            aiVoiceCallConsent: !!fullData.aiVoiceCallConsent,
            endlessHealthAcknowledgment: fullData.endlessHealthAcknowledgment || '',
            dataSharingPermission: fullData.dataSharingPermission || '',
            heartGuardChampionOptIn: fullData.heartGuardChampionOptIn || '',
            healthConcern: fullData.healthConcern || '',
            healthSupport: fullData.healthSupport || '',

          // Visit-based fields (only from active visit if present)
          systolicBP: latestVitalSign?.systolicBP,
          diastolicBP: latestVitalSign?.diastolicBP,
          heartRate: latestVitalSign?.heartRate,
          height: bodyMeasurements?.heightInches,
          weight: bodyMeasurements?.weightLbs,
          waistCircumference: bodyMeasurements?.waistInches,
          hipCircumference: bodyMeasurements?.hipInches,
          whr: bodyMeasurements?.whr,
          waistToHeightRatio: bodyMeasurements?.waistToHeightRatio,
          subcutaneousFat: bodyMeasurements?.subcutaneousFat,
          visceralFat: bodyMeasurements?.visceralFat,
          totalCholesterol: latestBloodTest?.totalCholesterol,
          hdlCholesterol: latestBloodTest?.hdlCholesterol,
          ldlCholesterol: latestBloodTest?.ldlCholesterol,
          triglycerides: latestBloodTest?.triglycerides,
          bloodGlucose: latestBloodTest?.bloodGlucose,
          a1c: latestBloodTest?.a1c,
          lipoproteinA: latestBloodTest?.lipoproteinA,
          apolipoproteinB: latestBloodTest?.apolipoproteinB,
          apolipoproteinA1: latestBloodTest?.apolipoproteinA1,
          creatinine: latestBloodTest?.creatinine,
          cReactiveProtein: latestBloodTest?.cReactiveProtein,
          fastingInsulin: latestBloodTest?.fastingInsulin,
          psa: latestBloodTest?.psa,
          eGFR: latestBloodTest?.eGFR,
          heartRhythm: latestEkgTest?.heartRhythm,
          qrsDuration: latestEkgTest?.qrsDuration,
          qtInterval: latestEkgTest?.qtInterval,
          ekgInterpretation: latestEkgTest?.interpretation,
          ekgAbnormalities: latestEkgTest?.abnormalities?.join(', '),
          clinicalNotes: activeVisit?.clinicalNotes,
          recommendations: activeVisit?.recommendations,
          followUpRequired: activeVisit?.followUpRequired || false,
          medicalReadings: medicalReadings,

          // Medical History
          priorHeartAttack: fullData.medicalHistory?.priorHeartAttack || false,
          priorStroke: fullData.medicalHistory?.priorStroke || false,
          angina: fullData.medicalHistory?.angina || false,
          heartFailure: fullData.medicalHistory?.heartFailure || false,
          atrialFibrillation: fullData.medicalHistory?.atrialFibrillation || false,
          peripheralArteryDisease: fullData.medicalHistory?.peripheralArteryDisease || false,
          carotidArteryDisease: fullData.medicalHistory?.carotidArteryDisease || false,
          diabetes: fullData.medicalHistory?.diabetes || false,
          hypertension: fullData.medicalHistory?.hypertension || false,
          highCholesterol: fullData.medicalHistory?.highCholesterol || false,
          chronicKidneyDisease: fullData.medicalHistory?.chronicKidneyDisease || false,
          thyroidDisease: fullData.medicalHistory?.thyroidDisease || false,
          familyHistoryHeartDisease: fullData.medicalHistory?.familyHistoryHeartDisease || false,
          familyHistoryEarlyHeartDisease: fullData.medicalHistory?.familyHistoryEarlyHeartDisease || false,
          familyHistoryStroke: fullData.medicalHistory?.familyHistoryStroke || false,
          familyHistoryDiabetes: fullData.medicalHistory?.familyHistoryDiabetes || false,
          familyHistoryHighBP: fullData.medicalHistory?.familyHistoryHighBP || false,

          // Medications
          aceInhibitors: fullData.medications?.aceInhibitors || false,
          arbBlockers: fullData.medications?.arbBlockers || false,
          betaBlockers: fullData.medications?.betaBlockers || false,
          calciumChannelBlockers: fullData.medications?.calciumChannelBlockers || false,
          diuretics: fullData.medications?.diuretics || false,
          statins: fullData.medications?.statins || false,
          fibrates: fullData.medications?.fibrates || false,
          ezetimibe: fullData.medications?.ezetimibe || false,
          pcsK9Inhibitors: fullData.medications?.pcsK9Inhibitors || false,
          metformin: fullData.medications?.metformin || false,
          insulin: fullData.medications?.insulin || false,
          sulfonylureas: fullData.medications?.sulfonylureas || false,
          sglt2Inhibitors: fullData.medications?.sglt2Inhibitors || false,
          glp1Agonists: fullData.medications?.glp1Agonists || false,
          aspirin: fullData.medications?.aspirin || false,
          clopidogrel: fullData.medications?.clopidogrel || false,
          warfarin: fullData.medications?.warfarin || false,
          doacs: fullData.medications?.doacs || false,
          nitrates: fullData.medications?.nitrates || false,
          digitalis: fullData.medications?.digitalis || false,
          antiarrhythmics: fullData.medications?.antiarrhythmics || false,

          // Additional fields
          usedBefore: fullData.usedBefore || false,
          testingCode: fullData.testingCode || '',
          bloodTestEnteredBy: fullData.bloodTestEnteredBy || '',
          bloodTestHandler: fullData.bloodTestHandler || ''
        };

        form.setFieldsValue(formValues);
        // Establish baseline snapshot for current (or edited) visit after values set
        try {
          const activeVisitId = editVisitId || currentVisitIdRef.current;
          const medReads = formValues.medicalReadings || [];
          const visitObj = medReads.find(v => v.visitId === activeVisitId);
          const exists = !!visitObj;
          if (exists) {
            visitBaselineRef.current = JSON.stringify(visitObj);
            setIsVisitDirty(false);
            setIsUnsavedNewVisit(false);
          } else {
            visitBaselineRef.current = null;
            setIsUnsavedNewVisit(recordsMode); // mark as new only in records mode
          }
          // Establish baseline for visit-scoped editable fields
          const visitFieldNames = [
            'systolicBP','diastolicBP','heartRate',
            'height','weight','waistCircumference','hipCircumference','whr','waistToHeightRatio','subcutaneousFat','visceralFat',
            'totalCholesterol','hdlCholesterol','ldlCholesterol','triglycerides','bloodGlucose','a1c','lipoproteinA','apolipoproteinB','apolipoproteinA1','creatinine','cReactiveProtein','fastingInsulin','psa','eGFR',
            'heartRhythm','qrsDuration','qtInterval','ekgInterpretation','ekgAbnormalities',
            'clinicalNotes','recommendations','followUpRequired'
          ];
          // Capture a baseline snapshot of all visit-scoped editable fields
          visitBaselineFieldsRef.current = form.getFieldsValue(visitFieldNames);
          // Mark initialization complete after baseline established
          // Use setTimeout to ensure child components (like MultipleReadingsTab) have also initialized
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 100);
        } catch(e) { /* ignore */ }
        if (editVisitId && currentVisitIdRef.current !== editVisitId) {
          currentVisitIdRef.current = editVisitId;
        }
        // Establish baseline after initial load
        try {
          const activeVisitId = currentVisitIdRef.current || editVisitId;
            establishBaselineIfNeeded(medicalReadings, activeVisitId);
        } catch(e){ /* noop */ }
      } catch (error) {
        console.error('Error fetching full patient data:', error);
        message.error('Failed to load complete patient information. Using available data.');
      } finally {
        setLoading(false);
      }
    };
    fetchFullPatientData();
    // Use stablePatientId instead of patient object to prevent re-initialization on parent re-renders (tab switches).
    // initialPatientData is included so the effect re-runs when the parent actually re-fetches fresh data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stablePatientId, editVisitId, initialPatientData]);
  
  const handleSave = async () => {
    if (readOnly) {
      message.info('Read-only mode: edits are disabled');
      return false;
    }
    if (isEgfrOutOfRange(form.getFieldValue('eGFR'))) {
      message.error(EGFR_RANGE_MESSAGE);
      return false;
    }
    // Get form values when saving - only save patient info, don't complete screening
    try {
      // Get ALL form fields, not just validated ones (to include hidden / inactive tab fields)
      const allFormValues = form.getFieldsValue(true);

      // Only validate core identity fields when on Member Information tab (tab key '3')
      if (activeTab === '3') {
        await form.validateFields(['firstName', 'lastName', 'emailAddress']);
      }
      // Use the full set as working values
      const formValues = { ...allFormValues };
      
      // Safety: ensure medicalReadings present if hidden field not captured
      if (!formValues.medicalReadings) {
        formValues.medicalReadings = form.getFieldValue('medicalReadings') || [];
        console.log('[SAVE] medicalReadings was not in allFormValues, fetched from form:', formValues.medicalReadings?.length || 0, 'visits');
      }
      
      // Debug: Log medicalReadings state for BP save verification
      console.log('[SAVE][BP] medicalReadings array status:', {
        totalVisits: formValues.medicalReadings?.length || 0,
        visits: (formValues.medicalReadings || []).map(v => ({
          visitId: v.visitId,
          vitalSignsCount: v.vitalSigns?.length || 0
        }))
      });
      
      setSaving(true);
      const actualPatientId = patient.patient?.patientId || patient.patientId || patient._id;
      
      // Merge visit-scoped data (vitals, labs, ekg, measurements, notes) into current visit record
      try {
        const targetVisitId = editVisitId || currentVisitIdRef.current;
        const medReads = Array.isArray(formValues.medicalReadings) ? [...formValues.medicalReadings] : [];
        let idx = medReads.findIndex(v => v.visitId === targetVisitId);
        
        // If no existing visit entry found, create a new one for this visit
        if (idx < 0 && targetVisitId) {
          console.log('[SAVE] Creating new visit entry for visitId:', targetVisitId);
          const newVisitEntry = {
            visitId: targetVisitId,
            visitDate: new Date(),
            eventId: patient?.eventId || patient?.bookingId,
            eventName: patient?.eventInfo?.eventTitle || 'Screening',
            location: patient?.eventInfo?.eventLocation || 'Location TBD',
            vitalSigns: [],
            bloodTests: [],
            ekgTests: [],
            bodyMeasurements: {},
            status: 'in_progress',
            createdAt: new Date()
          };
          medReads.push(newVisitEntry);
          idx = medReads.length - 1; // Point to the newly created entry
        }
        
        if (idx >= 0) {
          const visitCopy = { ...medReads[idx] };
          // Vital signs: in records mode (member-records) or when editing an existing visit,
          // the MultipleReadings array is the source of truth. Do NOT append from
          // top-level fields (they may still hold a mirrored copy of a deleted reading).
          // Only use the fallback append-from-top-level when creating a brand new visit
          // in queue mode (not records) and not editing a prior visit.
          if (!recordsMode && !editVisitId && formValues.systolicBP && formValues.diastolicBP && formValues.heartRate) {
            visitCopy.vitalSigns = Array.isArray(visitCopy.vitalSigns) ? [...visitCopy.vitalSigns] : [];
            // Avoid duplicate identical reading if last matches
            const last = visitCopy.vitalSigns[visitCopy.vitalSigns.length -1];
            const isDuplicate = last && last.systolicBP === formValues.systolicBP && last.diastolicBP === formValues.diastolicBP && last.heartRate === formValues.heartRate;
            if (!isDuplicate) {
              visitCopy.vitalSigns.push({
                systolicBP: formValues.systolicBP,
                diastolicBP: formValues.diastolicBP,
                heartRate: formValues.heartRate,
                timestamp: new Date(),
                position: 'sitting',
                readingNumber: (visitCopy.vitalSigns.length || 0) + 1
              });
            }
          }
          // Blood tests (single aggregate entry per save)
            const anyBloodField = [
              'totalCholesterol','hdlCholesterol','ldlCholesterol','triglycerides','bloodGlucose','a1c','lipoproteinA','apolipoproteinB','apolipoproteinA1','creatinine','cReactiveProtein','fastingInsulin','psa','eGFR'
            ].some(k => formValues[k] !== undefined && formValues[k] !== null && formValues[k] !== '');
            if (anyBloodField) {
              visitCopy.bloodTests = Array.isArray(visitCopy.bloodTests) ? [...visitCopy.bloodTests] : [];
              const bloodPayload = {
                testNumber: (visitCopy.bloodTests.length || 0) + 1,
                timestamp: new Date(),
                totalCholesterol: formValues.totalCholesterol,
                hdlCholesterol: formValues.hdlCholesterol,
                ldlCholesterol: formValues.ldlCholesterol,
                triglycerides: formValues.triglycerides,
                bloodGlucose: formValues.bloodGlucose,
                a1c: formValues.a1c,
                lipoproteinA: formValues.lipoproteinA,
                apolipoproteinB: formValues.apolipoproteinB,
                apolipoproteinA1: formValues.apolipoproteinA1,
                creatinine: formValues.creatinine,
                cReactiveProtein: formValues.cReactiveProtein,
                fastingInsulin: formValues.fastingInsulin,
                psa: formValues.psa,
                eGFR: formValues.eGFR
              };
              // Replace last incomplete record instead of always pushing new
              const lastBlood = visitCopy.bloodTests[visitCopy.bloodTests.length -1];
              if (lastBlood && !Object.values(lastBlood).some(v => v && typeof v === 'number')) {
                visitCopy.bloodTests[visitCopy.bloodTests.length -1] = bloodPayload;
              } else {
                visitCopy.bloodTests.push(bloodPayload);
              }
            } else if (Array.isArray(visitCopy.bloodTests) && visitCopy.bloodTests.length > 0) {
              // All blood test fields were cleared — remove blood test data from the visit
              visitCopy.bloodTests = [];
            }
          // EKG
          // NOTE: Previous logic used .some(k => formValues[k]) which skipped values that were empty strings initially being changed to a non-empty string one at a time.
          // We now consider a field "present" if it's not undefined AND not null AND not an empty string.
          const ekgFieldKeys = ['heartRhythm','qrsDuration','qtInterval','ekgInterpretation','ekgAbnormalities'];
          const ekgFieldPresent = (val) => val !== undefined && val !== null && !(typeof val === 'string' && val.trim() === '');
          const anyEkg = ekgFieldKeys.some(k => ekgFieldPresent(formValues[k]));
          
          console.log('[SAVE-EKG] EKG field values:', {
            heartRhythm: formValues.heartRhythm,
            qrsDuration: formValues.qrsDuration,
            qtInterval: formValues.qtInterval,
            ekgInterpretation: formValues.ekgInterpretation,
            ekgAbnormalities: formValues.ekgAbnormalities,
            anyEkg
          });
          
          if (anyEkg) {
            visitCopy.ekgTests = Array.isArray(visitCopy.ekgTests) ? [...visitCopy.ekgTests] : [];
            const newEkgTest = {
              testNumber: (visitCopy.ekgTests.length || 0) + 1,
              timestamp: new Date(),
              heartRhythm: formValues.heartRhythm || undefined,
              qrsDuration: formValues.qrsDuration !== undefined && formValues.qrsDuration !== null ? formValues.qrsDuration : undefined,
              qtInterval: formValues.qtInterval !== undefined && formValues.qtInterval !== null ? formValues.qtInterval : undefined,
              interpretation: formValues.ekgInterpretation || undefined,
              abnormalities: ekgFieldPresent(formValues.ekgAbnormalities) ? [formValues.ekgAbnormalities] : []
            };
            visitCopy.ekgTests.push(newEkgTest);
          } else if (Array.isArray(visitCopy.ekgTests) && visitCopy.ekgTests.length > 0) {
            // All EKG fields were cleared — remove EKG data from the visit
            visitCopy.ekgTests = [];
          }
          // Body measurements
          const anyBodyMeasurement = formValues.height || formValues.weight || formValues.waistCircumference || formValues.hipCircumference || formValues.whr || formValues.waistToHeightRatio || formValues.subcutaneousFat || formValues.visceralFat;
          if (anyBodyMeasurement) {
            visitCopy.bodyMeasurements = {
              heightInches: formValues.height === undefined ? visitCopy.bodyMeasurements?.heightInches : (formValues.height ?? undefined),
              weightLbs: formValues.weight === undefined ? visitCopy.bodyMeasurements?.weightLbs : (formValues.weight ?? undefined),
              waistInches: formValues.waistCircumference === undefined ? visitCopy.bodyMeasurements?.waistInches : (formValues.waistCircumference ?? undefined),
              hipInches: formValues.hipCircumference === undefined ? visitCopy.bodyMeasurements?.hipInches : (formValues.hipCircumference ?? undefined),
              whr: formValues.whr === undefined ? visitCopy.bodyMeasurements?.whr : (formValues.whr ?? undefined),
              waistToHeightRatio: formValues.waistToHeightRatio === undefined ? visitCopy.bodyMeasurements?.waistToHeightRatio : (formValues.waistToHeightRatio ?? undefined),
              subcutaneousFat: formValues.subcutaneousFat === undefined ? visitCopy.bodyMeasurements?.subcutaneousFat : (formValues.subcutaneousFat ?? undefined),
              visceralFat: formValues.visceralFat === undefined ? visitCopy.bodyMeasurements?.visceralFat : (formValues.visceralFat ?? undefined)
            };
          } else if (visitCopy.bodyMeasurements && Object.keys(visitCopy.bodyMeasurements).length > 0) {
            // All body measurement fields were cleared — remove body measurement data
            visitCopy.bodyMeasurements = {};
          }
          // Notes / recommendations
          const hadOriginalEkgCount = (visitCopy.ekgTests || []).length;
          const beforeNotes = visitCopy.clinicalNotes;
          const beforeRecs = visitCopy.recommendations;
          const beforeFollow = visitCopy.followUpRequired;
          
          if (formValues.clinicalNotes !== undefined) visitCopy.clinicalNotes = formValues.clinicalNotes;
          if (formValues.recommendations !== undefined) visitCopy.recommendations = formValues.recommendations;
          if (typeof formValues.followUpRequired === 'boolean') visitCopy.followUpRequired = formValues.followUpRequired;

          const changedOnlyTextual = (
            !anyBloodField &&
            !(formValues.systolicBP && formValues.diastolicBP && formValues.heartRate) &&
            !anyEkg &&
            !(formValues.height || formValues.weight || formValues.waistCircumference || formValues.hipCircumference || formValues.whr) && (
              beforeNotes !== visitCopy.clinicalNotes ||
              beforeRecs !== visitCopy.recommendations ||
              beforeFollow !== visitCopy.followUpRequired
            )
          );

          // Reassign ALWAYS if any targeted field changed (ensures persistence when only notes/recs/follow-up updated)
          const ekgCountDelta = (visitCopy.ekgTests || []).length !== hadOriginalEkgCount;
          if (ekgCountDelta || changedOnlyTextual || anyEkg) {
            console.log('[SAVE] Committing visit changes (ekg/textual). EKG added?', ekgCountDelta, 'textualOnly?', changedOnlyTextual);
          }
          console.log('[SAVE] Final visitCopy before assignment to medReads[idx]:', {
            visitId: visitCopy.visitId,
            ekgTests: visitCopy.ekgTests,
            clinicalNotes: visitCopy.clinicalNotes,
            recommendations: visitCopy.recommendations,
            followUpRequired: visitCopy.followUpRequired
          });
          medReads[idx] = visitCopy;
          formValues.medicalReadings = medReads;
        }
      } catch (e) {
        // Silent error handling for visit merge
        console.error('[SAVE] Error in visit merge logic:', e);
      }

      const updateData = {
        // Personal Information (only if on Member Information tab or has values)
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        emailAddress: formValues.emailAddress,
        phoneNumber: formValues.phoneNumber,
        dateOfBirth: formValues.dateOfBirth ? formValues.dateOfBirth.toISOString() : null,
        birthGender: formValues.birthGender,
        raceEthnicity: formValues.raceEthnicity,
        raceEthnicitySubgroup: formValues.raceEthnicitySubgroup,
        householdIncome: formValues.householdIncome,
        householdSize: formValues.householdSize,
        insuranceCoverage: formValues.insuranceCoverage,
        stateOfResidence: formValues.stateOfResidence,
        eventZip: formValues.eventZip,
          heartAttackOrStroke: formValues.heartAttackOrStroke || [],
          stressLevel: formValues.stressLevel || '',
          mentalHealthInterest: formValues.mentalHealthInterest || '',
          medicalConditions: formValues.medicalConditions,
          referredToCardiologist: formValues.referredToCardiologist,
          isParentOrCaregiver: formValues.isParentOrCaregiver || '',
          currentMedications: formValues.currentMedications || [],
          physicalActivityFrequency: formValues.physicalActivityFrequency || '',
          resourceAccess: formValues.resourceAccess || [],
          mediaRelease: formValues.mediaRelease,
          emergencyContactName: formValues.emergencyContactName,
          emergencyContactPhone: formValues.emergencyContactPhone,
          acknowledgeContraindications: formValues.acknowledgeContraindications,
          bhaWaiverConsent: formValues.bhaWaiverConsent || false,
          transactionalSmsConsent: !!formValues.transactionalSmsConsent,
          marketingSmsConsent: !!formValues.marketingSmsConsent,
          aiVoiceCallConsent: !!formValues.aiVoiceCallConsent,
          endlessHealthAcknowledgment: formValues.endlessHealthAcknowledgment || false,
          dataSharingPermission: formValues.dataSharingPermission,
          heartGuardChampionOptIn: formValues.heartGuardChampionOptIn,
          healthConcern: formValues.healthConcern || '',
          healthSupport: formValues.healthSupport || '',
          testingCode: formValues.testingCode || '',
          bloodTestEnteredBy: formValues.bloodTestEnteredBy || '',
          bloodTestHandler: formValues.bloodTestHandler || '',
        
        // Medical History - nested object
        // Preserve existing medicalHistory booleans unless we can infer from medicalConditions selections
        medicalHistory: (() => {
          const existing = (fullPatientData && fullPatientData.medicalHistory) || {};
          const selected = Array.isArray(formValues.medicalConditions) ? formValues.medicalConditions : [];
          const infer = (val) => selected.includes(val);
          return {
            priorHeartAttack: existing.priorHeartAttack || infer('heartProblems'),
            priorStroke: existing.priorStroke || false,
            angina: existing.angina || false,
            heartFailure: existing.heartFailure || false,
            atrialFibrillation: existing.atrialFibrillation || false,
            peripheralArteryDisease: existing.peripheralArteryDisease || false,
            carotidArteryDisease: existing.carotidArteryDisease || false,
            diabetes: infer('diabetes') || existing.diabetes || false,
            hypertension: infer('highBloodPressure') || existing.hypertension || false,
            highCholesterol: infer('badCholesterol') || existing.highCholesterol || false,
            chronicKidneyDisease: infer('kidneyDisease') || existing.chronicKidneyDisease || false,
            thyroidDisease: existing.thyroidDisease || false,
            familyHistoryHeartDisease: existing.familyHistoryHeartDisease || false,
            familyHistoryEarlyHeartDisease: existing.familyHistoryEarlyHeartDisease || false,
            familyHistoryStroke: existing.familyHistoryStroke || false,
            familyHistoryDiabetes: existing.familyHistoryDiabetes || false,
            familyHistoryHighBP: existing.familyHistoryHighBP || false,
            lastUpdated: new Date().toISOString()
          };
        })(),
        
        // Medications - nested object
        medications: {
          aceInhibitors: formValues.aceInhibitors || false,
          arbBlockers: formValues.arbBlockers || false,
          betaBlockers: formValues.betaBlockers || false,
          calciumChannelBlockers: formValues.calciumChannelBlockers || false,
          diuretics: formValues.diuretics || false,
          statins: formValues.statins || false,
          fibrates: formValues.fibrates || false,
          ezetimibe: formValues.ezetimibe || false,
          pcsK9Inhibitors: formValues.pcsK9Inhibitors || false,
          metformin: formValues.metformin || false,
          insulin: formValues.insulin || false,
          sulfonylureas: formValues.sulfonylureas || false,
          sglt2Inhibitors: formValues.sglt2Inhibitors || false,
          glp1Agonists: formValues.glp1Agonists || false,
          aspirin: formValues.aspirin || false,
          clopidogrel: formValues.clopidogrel || false,
          warfarin: formValues.warfarin || false,
          doacs: formValues.doacs || false,
          nitrates: formValues.nitrates || false,
          digitalis: formValues.digitalis || false,
          antiarrhythmics: formValues.antiarrhythmics || false,
          lastUpdated: new Date().toISOString()
        },
        
        // Screening Data - structure as nested object (only from Medical Testing tab)
        screeningData: {
          // Vital Signs (flat kept only if not editing existing visit – deprecated output otherwise)
          ...(editVisitId ? {} : { systolicBP: formValues.systolicBP, diastolicBP: formValues.diastolicBP, heartRate: formValues.heartRate }),
          totalCholesterol: editVisitId ? undefined : formValues.totalCholesterol,
          hdlCholesterol: editVisitId ? undefined : formValues.hdlCholesterol,
          ldlCholesterol: editVisitId ? undefined : formValues.ldlCholesterol,
          triglycerides: editVisitId ? undefined : formValues.triglycerides,
          bloodGlucose: editVisitId ? undefined : formValues.bloodGlucose,
          a1c: editVisitId ? undefined : formValues.a1c,
          lipoproteinA: editVisitId ? undefined : formValues.lipoproteinA,
          apolipoproteinB: editVisitId ? undefined : formValues.apolipoproteinB,
          apolipoproteinA1: editVisitId ? undefined : formValues.apolipoproteinA1,
          creatinine: editVisitId ? undefined : formValues.creatinine,
          cReactiveProtein: editVisitId ? undefined : formValues.cReactiveProtein,
          fastingInsulin: editVisitId ? undefined : formValues.fastingInsulin,
          psa: editVisitId ? undefined : formValues.psa,
          eGFR: editVisitId ? undefined : formValues.eGFR,
          heartRhythm: editVisitId ? undefined : formValues.heartRhythm,
          qrsDuration: editVisitId ? undefined : formValues.qrsDuration,
          qtInterval: editVisitId ? undefined : formValues.qtInterval,
          ekgInterpretation: editVisitId ? undefined : formValues.ekgInterpretation,
          ekgAbnormalities: editVisitId ? undefined : formValues.ekgAbnormalities,
          clinicalNotes: editVisitId ? undefined : formValues.clinicalNotes,
          recommendations: editVisitId ? undefined : formValues.recommendations,
          followUpRequired: editVisitId ? undefined : (formValues.followUpRequired || false),
          medicalReadings: formValues.medicalReadings,
          lastUpdated: new Date().toISOString()
        }
      };

      // Remove undefined/null/empty values to avoid sending empty data
      // Fields where empty string is a valid "clear" value (user intentionally blanked them)
      const CLEARABLE_FIELDS = [
        'testingCode', 'bloodTestEnteredBy', 'bloodTestHandler', 'householdIncome', 'householdSize',
        'isParentOrCaregiver', 'physicalActivityFrequency',
        'healthConcern', 'healthSupport',
        'emergencyContactName', 'emergencyContactPhone'
      ];
      // Nested fields inside screeningData that should be sent as null/empty to clear old values
      const CLEARABLE_NESTED_FIELDS = [
        'totalCholesterol', 'hdlCholesterol', 'ldlCholesterol',
        'triglycerides', 'bloodGlucose', 'a1c', 'lipoproteinA', 'apolipoproteinB', 'apolipoproteinA1', 'creatinine', 'cReactiveProtein', 'fastingInsulin', 'psa', 'eGFR',
        'systolicBP', 'diastolicBP', 'heartRate',
        'heartRhythm', 'qrsDuration', 'qtInterval',
        'ekgInterpretation', 'ekgAbnormalities'
      ];
      const cleanUpdateData = {};
      Object.keys(updateData).forEach(key => {
        if (key === 'screeningData' || key === 'medicalHistory' || key === 'medications') {
          // Handle nested objects (screeningData, medicalHistory, medications)
          const cleanNestedData = {};
          Object.keys(updateData[key]).forEach(nestedKey => {
            const value = updateData[key][nestedKey];
            // Keep arrays (like medicalReadings) even if empty
            if (Array.isArray(value)) {
              cleanNestedData[nestedKey] = value;
            }
            // Keep boolean false values, but skip undefined/null
            else if (value !== undefined && value !== null && value !== '') {
              cleanNestedData[nestedKey] = value;
            } else if (value === false) {
              // Explicitly include false for checkboxes
              cleanNestedData[nestedKey] = value;
            } else if ((value === null || value === '') && CLEARABLE_NESTED_FIELDS.includes(nestedKey)) {
              // Allow clearing blood test, vital sign, and EKG fields
              cleanNestedData[nestedKey] = null;
            }
          });
          if (Object.keys(cleanNestedData).length > 0) {
            cleanUpdateData[key] = cleanNestedData;
          }
        } else if (updateData[key] !== undefined && updateData[key] !== null &&
                   (updateData[key] !== '' || CLEARABLE_FIELDS.includes(key))) {
          cleanUpdateData[key] = updateData[key];
        }
      });

      await PatientService.updatePatient(actualPatientId, cleanUpdateData);
      
      message.success('Patient information saved successfully');
      // Reset baseline after successful save in records mode
      if (recordsMode) {
        try {
          const targetVisitId = editVisitId || currentVisitIdRef.current;
          const updatedReads = formValues.medicalReadings || [];
          const visitObj = updatedReads.find(v => v.visitId === targetVisitId);
          if (visitObj) {
            visitBaselineRef.current = JSON.stringify(visitObj);
            setIsVisitDirty(false);
            setIsUnsavedNewVisit(false);
          }
        } catch(e){ /* noop */ }
      }
      // Reset unsaved flag once save succeeds in records mode
      if (recordsMode && hasUnsavedNewReading) {
        // Refresh baseline to include all current readings for the active visit
        try {
          const medicalReadingsArr = formValues.medicalReadings || [];
          const activeVisitId = editVisitId || currentVisitIdRef.current;
          establishBaselineIfNeeded(medicalReadingsArr, activeVisitId);
        } catch(e){ setHasUnsavedNewReading(false); }
      }
      
      // In records mode, refresh internal patient data so tab switches show saved values
      if (recordsMode) {
        try {
          const refreshId = patient.patient?.patientId || patient.patientId || patient._id;
          const refreshRes = await PatientService.getPatientDetails(refreshId);
          if (refreshRes?.data) {
            setFullPatientData(refreshRes.data);
            // Re-set form values from fresh data so all tabs stay in sync
            const freshData = refreshRes.data;
            const freshReadings = freshData.screeningData?.medicalReadings || [];
            const freshVisit = freshReadings.find(v => v.visitId === (editVisitId || currentVisitIdRef.current));
            const freshVital = freshVisit?.vitalSigns?.[freshVisit.vitalSigns.length - 1];
            const freshBlood = freshVisit?.bloodTests?.[freshVisit.bloodTests.length - 1];
            const freshEkg = freshVisit?.ekgTests?.[freshVisit.ekgTests.length - 1];
            const freshBody = freshVisit?.bodyMeasurements || {};
            form.setFieldsValue({
              // Demographics from fresh server data
              firstName: freshData.firstName,
              lastName: freshData.lastName,
              emailAddress: freshData.emailAddress,
              phoneNumber: freshData.phoneNumber,
              dateOfBirth: freshData.dateOfBirth ? moment(freshData.dateOfBirth) : null,
              testingCode: freshData.testingCode || '',
              bloodTestEnteredBy: freshData.bloodTestEnteredBy || '',
              bloodTestHandler: freshData.bloodTestHandler || '',
              householdIncome: freshData.householdIncome,
              householdSize: freshData.householdSize,
              isParentOrCaregiver: freshData.isParentOrCaregiver || '',
              physicalActivityFrequency: freshData.physicalActivityFrequency || '',
              healthConcern: freshData.healthConcern || '',
              healthSupport: freshData.healthSupport || '',
              emergencyContactName: freshData.emergencyContactName || '',
              emergencyContactPhone: freshData.emergencyContactPhone || '',
              // Visit-scoped fields from fresh data
              systolicBP: freshVital?.systolicBP,
              diastolicBP: freshVital?.diastolicBP,
              heartRate: freshVital?.heartRate,
              height: freshBody?.heightInches,
              weight: freshBody?.weightLbs,
              waistCircumference: freshBody?.waistInches,
              hipCircumference: freshBody?.hipInches,
              whr: freshBody?.whr,
              waistToHeightRatio: freshBody?.waistToHeightRatio,
              subcutaneousFat: freshBody?.subcutaneousFat,
              visceralFat: freshBody?.visceralFat,
              totalCholesterol: freshBlood?.totalCholesterol,
              hdlCholesterol: freshBlood?.hdlCholesterol,
              ldlCholesterol: freshBlood?.ldlCholesterol,
              triglycerides: freshBlood?.triglycerides,
              bloodGlucose: freshBlood?.bloodGlucose,
              a1c: freshBlood?.a1c,
              lipoproteinA: freshBlood?.lipoproteinA,
              apolipoproteinB: freshBlood?.apolipoproteinB,
              apolipoproteinA1: freshBlood?.apolipoproteinA1,
              creatinine: freshBlood?.creatinine,
              cReactiveProtein: freshBlood?.cReactiveProtein,
              fastingInsulin: freshBlood?.fastingInsulin,
              psa: freshBlood?.psa,
              eGFR: freshBlood?.eGFR,
              heartRhythm: freshEkg?.heartRhythm,
              qrsDuration: freshEkg?.qrsDuration,
              qtInterval: freshEkg?.qtInterval,
              ekgInterpretation: freshEkg?.interpretation,
              ekgAbnormalities: freshEkg?.abnormalities?.join(', '),
              clinicalNotes: freshVisit?.clinicalNotes,
              recommendations: freshVisit?.recommendations,
              followUpRequired: freshVisit?.followUpRequired || false,
              medicalReadings: freshReadings,
            });
            // Re-establish baselines with fresh data
            const targetVid = editVisitId || currentVisitIdRef.current;
            const freshVisitObj = freshReadings.find(v => v.visitId === targetVid);
            if (freshVisitObj) {
              visitBaselineRef.current = JSON.stringify(freshVisitObj);
              setIsVisitDirty(false);
              setIsUnsavedNewVisit(false);
            }
            establishBaselineIfNeeded(freshReadings, targetVid);
          }
        } catch (refreshErr) {
          console.warn('[SAVE] Post-save data refresh failed (non-critical):', refreshErr);
        }
      }

      // Return success to indicate the save was completed
      return true;
      
    } catch (error) {
      console.error('Error updating patient:', error);
      message.error('Failed to save patient information. Please try again.');
      throw error; // Re-throw the error so calling functions can handle it
    } finally {
      setSaving(false);
    }
  };

  // Save progress without completing - keeps patient in Active Queue
  const handleSaveProgress = async () => {
    if (readOnly) {
      message.info('Read-only mode: edits are disabled');
      return;
    }
    
    try {
      // First save the patient data using existing logic
      if (!(await handleSave())) return;
      
      // Determine which sections were updated based on form values
      const formValues = form.getFieldsValue(true);
      const sectionsUpdated = [];
      
      if (formValues.systolicBP || formValues.diastolicBP || formValues.heartRate) {
        sectionsUpdated.push('vitals');
      }
      if (formValues.totalCholesterol || formValues.hdlCholesterol || formValues.bloodGlucose || formValues.a1c || formValues.lipoproteinA || formValues.apolipoproteinB || formValues.apolipoproteinA1 || formValues.creatinine || formValues.cReactiveProtein || formValues.fastingInsulin || formValues.psa || formValues.eGFR) {
        sectionsUpdated.push('bloodTests');
      }
      if (formValues.height || formValues.weight || formValues.waistCircumference) {
        sectionsUpdated.push('bodyMeasurements');
      }
      if (formValues.heartRhythm || formValues.qrsDuration) {
        sectionsUpdated.push('ekg');
      }
      if (formValues.clinicalNotes || formValues.recommendations) {
        sectionsUpdated.push('notes');
      }
      
      // Record the partial save on the visit
      const queueId = patient._id;
      await VisitService.saveProgress(queueId, sectionsUpdated);
      
      message.success('Progress saved - patient remains in queue');
      
      if (onSuccess) {
        onSuccess(patient, false); // false = not completing
      }
      
      onBack(); // Navigate back to queue
    } catch (error) {
      console.error('Error saving progress:', error);
      message.error('Failed to save progress. Please try again.');
    }
  };

  const handleCompleteScreening = async () => {
    if (isEgfrOutOfRange(form.getFieldValue('eGFR'))) {
      message.error(EGFR_RANGE_MESSAGE);
      return;
    }
    // Complete the screening and mark patient as completed
    try {
      // Validate minimal required fields (member identity) but then pull ALL values including hidden ones
      try { await form.validateFields(['firstName','lastName']); } catch(e) { /* allow completion even if identity not edited */ }
      // Pull comprehensive form values
      const formValues = form.getFieldsValue(true);
      if (!formValues.medicalReadings) {
        formValues.medicalReadings = form.getFieldValue('medicalReadings') || [];
      }
      setCompleting(true);
      
      // First save all the patient data (inline the save logic to ensure proper async handling)
      const actualPatientId = patient.patient?.patientId || patient.patientId || patient._id;
      // Use stable visit ID established earlier (ensures we don't regenerate and lose linkage)
      const stableVisitId = currentVisitIdRef.current || patient?.visitId || patient?._id || `visit_${Date.now()}`;
      
  // Defensive copy of form medicalReadings before mutation
  const existingFormMedicalReadings = Array.isArray(formValues.medicalReadings) ? JSON.parse(JSON.stringify(formValues.medicalReadings)) : [];

      const updateData = {
        // Personal Information
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        emailAddress: formValues.emailAddress,
        phoneNumber: formValues.phoneNumber,
        dateOfBirth: formValues.dateOfBirth ? formValues.dateOfBirth.toISOString() : null,
        birthGender: formValues.birthGender,
        
        // Demographics
        raceEthnicity: formValues.raceEthnicity,
        raceEthnicitySubgroup: formValues.raceEthnicitySubgroup,
        householdIncome: formValues.householdIncome,
        householdSize: formValues.householdSize,
        insuranceCoverage: formValues.insuranceCoverage,
        stateOfResidence: formValues.stateOfResidence,
        eventZip: formValues.eventZip,
        medicalConditions: formValues.medicalConditions,
        referredToCardiologist: formValues.referredToCardiologist,
        isParentOrCaregiver: formValues.isParentOrCaregiver || '',
        currentMedications: formValues.currentMedications || [],
        physicalActivityFrequency: formValues.physicalActivityFrequency || '',
        resourceAccess: formValues.resourceAccess || [],
        mediaRelease: formValues.mediaRelease,
        emergencyContactName: formValues.emergencyContactName,
        emergencyContactPhone: formValues.emergencyContactPhone,
        acknowledgeContraindications: formValues.acknowledgeContraindications,
        bhaWaiverConsent: formValues.bhaWaiverConsent || false,
        transactionalSmsConsent: !!formValues.transactionalSmsConsent,
        marketingSmsConsent: !!formValues.marketingSmsConsent,
        aiVoiceCallConsent: !!formValues.aiVoiceCallConsent,
        endlessHealthAcknowledgment: formValues.endlessHealthAcknowledgment || false,
        dataSharingPermission: formValues.dataSharingPermission,
        heartGuardChampionOptIn: formValues.heartGuardChampionOptIn,
        healthConcern: formValues.healthConcern || '',
        healthSupport: formValues.healthSupport || '',
        
        // Screening Data - NEW STRUCTURE: Visit-based organization
        screeningData: {
          // Visit-based data - preserve prior visits and append/update current stable visit
          medicalReadings: (() => {
            const existingReadings = Array.isArray(existingFormMedicalReadings) ? [...existingFormMedicalReadings] : [];
            let visitIndex = existingReadings.findIndex(v => v.visitId === stableVisitId);

            // Pull any existing vitalSigns for stable visit to avoid overwriting
            const existingVisitVitalSigns = visitIndex >= 0 ? (existingReadings[visitIndex].vitalSigns || []) : [];
            const formVisitVitalSigns = formValues.medicalReadings?.find(v => v.visitId === stableVisitId)?.vitalSigns || [];
            // Merge by id (preserve order of existing + append new ones not present)
            const mergedVitalSignsMap = new Map();
            [...existingVisitVitalSigns, ...formVisitVitalSigns].forEach((r, idx) => {
              const id = r.id || r._id || `${stableVisitId}_${r.timestamp || idx}_${idx}`;
              if (!mergedVitalSignsMap.has(id)) {
                mergedVitalSignsMap.set(id, { ...r, id });
              }
            });
            const mergedVitalSigns = Array.from(mergedVitalSignsMap.values()).map((r, i) => ({
              ...r,
              readingNumber: r.readingNumber || i + 1
            }));

            const currentVisitData = {
              visitId: stableVisitId,
              visitDate: new Date(),
              eventId: patient?.eventId,
              eventName: patient?.eventInfo?.eventTitle || 'Current Screening',
              location: patient?.eventInfo?.eventLocation || 'Current Location',
              vitalSigns: mergedVitalSigns,
              bloodTests: [{
                testNumber: 1,
                timestamp: new Date(),
                totalCholesterol: formValues.totalCholesterol,
                hdlCholesterol: formValues.hdlCholesterol,
                ldlCholesterol: formValues.ldlCholesterol,
                triglycerides: formValues.triglycerides,
                bloodGlucose: formValues.bloodGlucose,
                a1c: formValues.a1c,
                lipoproteinA: formValues.lipoproteinA,
                apolipoproteinB: formValues.apolipoproteinB,
                apolipoproteinA1: formValues.apolipoproteinA1,
                creatinine: formValues.creatinine,
                cReactiveProtein: formValues.cReactiveProtein,
                fastingInsulin: formValues.fastingInsulin,
                psa: formValues.psa,
                eGFR: formValues.eGFR
              }].filter(test => test.totalCholesterol || test.hdlCholesterol || test.ldlCholesterol || test.triglycerides || test.bloodGlucose || test.a1c || test.lipoproteinA || test.apolipoproteinB || test.apolipoproteinA1 || test.creatinine || test.cReactiveProtein || test.fastingInsulin || test.psa || test.eGFR),
              ekgTests: [{
                testNumber: 1,
                timestamp: new Date(),
                heartRhythm: formValues.heartRhythm || undefined,
                qrsDuration: formValues.qrsDuration || undefined,
                qtInterval: formValues.qtInterval || undefined,
                interpretation: formValues.ekgInterpretation || undefined,
                abnormalities: formValues.ekgAbnormalities ? [formValues.ekgAbnormalities] : []
              }].filter(test => test.heartRhythm || test.qrsDuration || test.qtInterval || test.interpretation),
              bodyMeasurements: {
                heightInches: formValues.height,
                weightLbs: formValues.weight,
                waistInches: formValues.waistCircumference,
                hipInches: formValues.hipCircumference,
                whr: formValues.whr,
                waistToHeightRatio: formValues.waistToHeightRatio,
                subcutaneousFat: formValues.subcutaneousFat,
                visceralFat: formValues.visceralFat
              },
              clinicalNotes: formValues.clinicalNotes,
              recommendations: formValues.recommendations,
              followUpRequired: formValues.followUpRequired || false,
              completedAt: new Date(),
              completedBy: 'Current User'
            };

            if ((currentVisitData.vitalSigns.length === 0) && formValues.systolicBP && formValues.diastolicBP && formValues.heartRate) {
              currentVisitData.vitalSigns = [{
                systolicBP: formValues.systolicBP,
                diastolicBP: formValues.diastolicBP,
                heartRate: formValues.heartRate,
                timestamp: new Date(),
                position: 'sitting',
                readingNumber: 1,
                id: `synthetic_${Date.now()}`
              }];
            }

            // Merge strategy: if visit exists, always merge/replace in place (do NOT duplicate on return visits)
            if (visitIndex >= 0) {
              const prev = existingReadings[visitIndex];
              // Preserve earlier vitalSigns if we somehow lost them
              if (!currentVisitData.vitalSigns.length && prev.vitalSigns?.length) {
                currentVisitData.vitalSigns = prev.vitalSigns;
              }
              existingReadings[visitIndex] = currentVisitData;
            } else if (visitIndex === -1) {
              existingReadings.push(currentVisitData);
            }
            return existingReadings;
          })(),
          
          lastUpdated: new Date().toISOString()
        }
      };

      // Also merge back inferred legacy medicalHistory booleans so completion does not wipe them
      updateData.medicalHistory = (() => {
        const existing = (fullPatientData && fullPatientData.medicalHistory) || {};
        const selected = Array.isArray(formValues.medicalConditions) ? formValues.medicalConditions : [];
        const infer = (val) => selected.includes(val);
        return {
          ...existing,
            priorHeartAttack: existing.priorHeartAttack || infer('heartProblems'),
            diabetes: infer('diabetes') || existing.diabetes || false,
            hypertension: infer('highBloodPressure') || existing.hypertension || false,
            highCholesterol: infer('badCholesterol') || existing.highCholesterol || false,
            chronicKidneyDisease: infer('kidneyDisease') || existing.chronicKidneyDisease || false,
            lastUpdated: new Date().toISOString()
        };
      })();

      // Clean the data - remove undefined/null/empty values
      // Nested fields inside screeningData that should be sent as null/empty to clear old values
      const CLEARABLE_NESTED_FIELDS = [
        'totalCholesterol', 'hdlCholesterol', 'ldlCholesterol',
        'triglycerides', 'bloodGlucose', 'a1c', 'lipoproteinA', 'apolipoproteinB', 'apolipoproteinA1', 'creatinine', 'cReactiveProtein', 'fastingInsulin', 'psa', 'eGFR',
        'systolicBP', 'diastolicBP', 'heartRate',
        'heartRhythm', 'qrsDuration', 'qtInterval',
        'ekgInterpretation', 'ekgAbnormalities'
      ];
      const cleanUpdateData = {};
      Object.keys(updateData).forEach(key => {
        if (key === 'screeningData') {
          // Handle nested screeningData object
          const cleanScreeningData = {};
          Object.keys(updateData[key]).forEach(nestedKey => {
            const value = updateData[key][nestedKey];
            // Always keep arrays (medicalReadings is now the main structure)
            if (Array.isArray(value)) {
              cleanScreeningData[nestedKey] = value;
            }
            else if (value !== undefined && value !== null && value !== '') {
              cleanScreeningData[nestedKey] = value;
            } else if ((value === null || value === '') && CLEARABLE_NESTED_FIELDS.includes(nestedKey)) {
              // Allow clearing blood test, vital sign, and EKG fields
              cleanScreeningData[nestedKey] = null;
            }
          });
          if (Object.keys(cleanScreeningData).length > 0) {
            cleanUpdateData[key] = cleanScreeningData;
          }
        } else if (updateData[key] !== undefined && updateData[key] !== null && updateData[key] !== '') {
          cleanUpdateData[key] = updateData[key];
        }
      });
      
      // Save the patient data first
      await PatientService.updatePatient(actualPatientId, cleanUpdateData);
      
      // Complete the screening (sets status to 'completed', records timing, sends notification)
      const queueId = patient._id;
      try {
        await VisitService.completeScreening(queueId);
      } catch (timingError) {
        // If completeScreening fails (e.g. already completed), fall back to direct status update
        await PatientService.updateQueueStatus(queueId, { 
          status: 'completed',
          completionTime: new Date().toISOString()
        });
      }
      
      // Trigger the station status update instead of the context
      try {
        // Get the booking/event ID from the patient data
        const bookingId = patient.eventId || patient.event?._id || patient.booking?._id;
        
        if (bookingId) {
          await StationService.completeScreening(bookingId);
        }
      } catch (error) {
        // Don't fail the whole operation if status update fails
      }
      
      if (onSuccess) {
        onSuccess(patient, true); // Pass true to indicate completion
      }
      
      onBack(); // Navigate back to queue
    } catch (error) {
      console.error('Error completing screening:', error);
      message.error('Failed to complete screening. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  if (!patient) return null;

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <HeartLoader size="48px" text="Loading complete patient information..." padding="0" />
        </LoadingContainer>
      </Container>
    );
  }

  const patientData = fullPatientData || patient.patient || patient;

  return (
    <Container>
      {!suppressHeader && (
        <HeaderContainer>
          <HeaderWrapper>
            <TitleSection>
              <PatientNameRow>
                <PatientName>
                  {patientData?.firstName && patientData?.lastName ? 
                    `${patientData.firstName} ${patientData.lastName}` : 
                    'Loading patient details...'
                  }
                </PatientName>
                <NameDivider />
                <PatientInlineId>
                  ID: {(patient._id || patient.patientId || 'N/A').toString().slice(-6).toUpperCase()}
                </PatientInlineId>
                {patient?.status && (
                  <StatusBadge status={patient.status}>
                    {patient.status.replace('_', ' ')}
                  </StatusBadge>
                )}
                {/* Display risk score if available */}
                {(() => {
                  const readings = fullPatientData?.screeningData?.medicalReadings || [];
                  const latestVisit = readings.length > 0 ? readings[readings.length - 1] : null;
                  if (latestVisit?.normalizedRiskScore !== undefined && latestVisit?.normalizedRiskScore !== null) {
                    return (
                      <RiskScoreBadge 
                        level={latestVisit.riskLevel || 'unknown'}
                        title={`Risk Score: ${latestVisit.normalizedRiskScore}/100 (${latestVisit.riskLevel || 'unknown'})`}
                      >
                        {latestVisit.normalizedRiskScore}
                      </RiskScoreBadge>
                    );
                  }
                  return null;
                })()}
              </PatientNameRow>
            </TitleSection>
          </HeaderWrapper>
        </HeaderContainer>
      )}

      {/* Info banner showing last save if patient has partial saves */}
      {showCompleteButton && patient?.lastSavedAt && !patient?.completionTime && !recordsMode && (
        <Alert
          message={
            <span>
              <SaveOutlined style={{ marginRight: 8 }} />
              Last saved by {patient.lastSavedByName || 'Staff'} at {new Date(patient.lastSavedAt).toLocaleTimeString()}
            </span>
          }
          type="info"
          showIcon={false}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={saving} indicator={<HeartLoader size="32px" padding="20px" />}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          autoComplete="off"
          onValuesChange={(changedValues, allValues) => {
            if (changedValues.waistCircumference || changedValues.height) {
              const waist = allValues.waistCircumference;
              const height = allValues.height;
              if (waist && height && waist > 0 && height > 0) {
                const whtr = (waist / height).toFixed(2);
                form.setFieldsValue({ waistToHeightRatio: parseFloat(whtr) });
              }
            }
            // Dirty tracking for records mode (any visit edits)
            if (recordsMode) {
              // Skip dirty check during initial load
              if (isInitializingRef.current) {
                return;
              }
              try {
                const targetVisitId = editVisitId || currentVisitIdRef.current;
                const medReads = form.getFieldValue('medicalReadings') || [];
                const visitObj = medReads.find(v => v.visitId === targetVisitId);
                let medicalReadingsChanged = false;
                if (visitObj && visitBaselineRef.current) {
                  const currentSnap = JSON.stringify(visitObj);
                  medicalReadingsChanged = (currentSnap !== visitBaselineRef.current);
                }
                // Field-level diff (covers labs, ekg, notes, etc. that aren't yet inside medicalReadings until save)
                const visitFieldNames = [
                  'systolicBP','diastolicBP','heartRate',
                  'height','weight','waistCircumference','hipCircumference','whr','waistToHeightRatio',
                  'totalCholesterol','hdlCholesterol','ldlCholesterol','triglycerides','bloodGlucose','a1c','lipoproteinA','apolipoproteinB','apolipoproteinA1','creatinine','cReactiveProtein','fastingInsulin','psa','eGFR',
                  'heartRhythm','qrsDuration','qtInterval','ekgInterpretation','ekgAbnormalities',
                  'clinicalNotes','recommendations','followUpRequired'
                ];
                const currentFieldSubset = form.getFieldsValue(visitFieldNames);
                const baselineSubset = visitBaselineFieldsRef.current || {};
                const fieldChanged = visitFieldNames.some(fn => {
                  const a = baselineSubset[fn];
                  const b = currentFieldSubset[fn];
                  if (a === undefined && (b === undefined || b === null || b === '')) return false;
                  if (b === undefined && (a === undefined || a === null || a === '')) return false;
                  return JSON.stringify(a) !== JSON.stringify(b);
                });
                setIsVisitDirty(medicalReadingsChanged || fieldChanged);
              } catch(e) { /* ignore */ }
            }
          }}
          size="large"
        >
          {/* Hidden Form.Item to ensure medicalReadings persists in form state.
              Without this, form.getFieldsValue(true) may not capture medicalReadings
              because Ant Design Form requires a Form.Item to track field values. */}
          <Form.Item name="medicalReadings" hidden noStyle>
            <input type="hidden" />
          </Form.Item>

          {/* Top pill tabs (hide when side menu controls navigation) */}
          {!hideTabs && (() => {
            const medicalReadings = fullPatientData?.screeningData?.medicalReadings || [];
            const tabs = [
              { key:'1', icon:<FontAwesomeIcon icon={faStethoscope} />, label:'Medical Testing' },
              ...(medicalReadings.length > 1 ? [{ key:'4', icon:<FontAwesomeIcon icon={faChartLine} />, label:'Trends' }] : []),
              { key:'5', icon:<FontAwesomeIcon icon={faClockRotateLeft} />, label:'Visit History' },
              { key:'3', icon:<FontAwesomeIcon icon={faUser} />, label:'Member Information' },
              { key:'6', icon:<FontAwesomeIcon icon={faCalendarDays} />, label:'Event' },
              ...(canMessage ? [{ key:'7', icon:<FontAwesomeIcon icon={faCommentDots} />, label:'Messaging' }] : []),
              ...(canEmail ? [{ key:'8', icon:<FontAwesomeIcon icon={faEnvelope} />, label:'Email' }] : [])
            ];
            return (
              <EditTabsContainer>
                {tabs.map(t => (
                  <EditTabButton
                    key={t.key}
                    $active={activeTab === t.key}
                    onClick={() => {
                      if (onActiveTabChange) onActiveTabChange(t.key);
                      if (!forcedActiveTab) setInternalActiveTab(t.key);
                    }}
                  >
                    {t.icon} {t.label}
                  </EditTabButton>
                ))}
              </EditTabsContainer>
            );
          })()}

          {/* Content Card */}
          <FormCard
            style={{ marginTop: 0 }}
            styles={activeTab === '7' || activeTab === '8' ? { body: { padding: 0, overflow: 'hidden' } } : undefined}
          >
            {(() => {
              const medicalReadings = fullPatientData?.screeningData?.medicalReadings || [];
              if (fullPatientData && !currentVisitIdRef.current) {
                const isReturnVisit = fullPatientData?.status === 'completed' || medicalReadings.length > 0;
                currentVisitIdRef.current = isReturnVisit 
                  ? `visit_${new Date().toISOString().split('T')[0].replace(/-/g, '')}_${Math.random().toString(36).substr(2, 9)}`
                  : (patient?.visitId || patient?._id);
              }
              const stableVisitId = currentVisitIdRef.current || (patient?.visitId || patient?._id || 'current');
              switch(activeTab) {
                case '1':
                  return <MedicalTestingTab 
                    form={form} 
                    patient={fullPatientData}
                    currentVisit={{
                      visitId: stableVisitId,
                      eventId: patient?.eventId,
                      eventName: patient?.eventInfo?.eventTitle || 'Current Screening',
                      location: patient?.eventInfo?.eventLocation || 'Current Location'
                    }}
                    recordsMode={recordsMode}
                    onVisitContextChange={({ isHistorical }) => setIsViewingHistoricalVisit(!!isHistorical)}
                    onReadingsChange={(changeData) => {
                      // When readings change, mark visit as dirty in records mode
                      // Skip if still initializing (parent baseline not yet established)
                      if (isInitializingRef.current) {
                        return;
                      }
                      if (recordsMode) {
                        setIsVisitDirty(true);
                        if (changeData?.type === 'added' && !autoSavingRef.current && !saving && !completing) {
                          autoSavingRef.current = true;
                          (async () => {
                            try {
                              await handleSave();
                            } finally {
                              autoSavingRef.current = false;
                            }
                          })();
                        }
                      }
                    }}
                  />;
                case '5':
                  return <VisitHistoryTab
                    visits={fullPatientData?.screeningData?.medicalReadings || []}
                    currentVisitId={stableVisitId}
                    onSelectVisit={() => {
                      if (!forcedActiveTab) setInternalActiveTab('1');
                      if (onActiveTabChange) onActiveTabChange('1');
                    }}
                  />;
                // BP Readings tab removed; component embedded in MedicalTestingTab
                case '4':
                  return <TrendsTab visits={fullPatientData?.screeningData?.medicalReadings || []} />;
                case '6':
                  return <EventChangeTab 
                    patient={patient}
                    onEventChanged={() => {
                      // Call onSuccess to refresh patient list and data
                      if (onSuccess) onSuccess();
                    }}
                  />;
                case '7':
                  if (!canMessage) return <PatientInfoTab patientData={fullPatientData} />;
                  return <PatientConversation
                    phoneNumber={fullPatientData?.phoneNumber}
                    contactName={`${fullPatientData?.firstName || ''} ${fullPatientData?.lastName || ''}`.trim()}
                  />;
                case '8':
                  if (!canEmail) return <PatientInfoTab patientData={fullPatientData} />;
                  return <PatientEmailPanel
                    patientId={fullPatientData?.patientId}
                    name={`${fullPatientData?.firstName || ''} ${fullPatientData?.lastName || ''}`.trim()}
                    email={fullPatientData?.emailAddress}
                    phone={fullPatientData?.phoneNumber}
                  />;
                case '3':
                default:
                  return <PatientInfoTab patientData={fullPatientData} />;
              }
            })()}

            {activeTab !== '7' && (
            <FormFooter>
              <Space size="large">
                {activeTab === '3' && (
                  <>
                    <Button 
                      size="large"
                      onClick={onBack}
                      disabled={saving}
                      style={{ height: '52px' }}
                    >
                      Cancel
                    </Button>
                    <SaveButton 
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={saving}
                      icon={<SaveOutlined />}
                      style={{ color: 'white' }}
                    >
                      Save Changes
                    </SaveButton>
                  </>
                )}
                {/* Records mode buttons (Member Records page) */}
                {activeTab === '1' && recordsMode && !showCompleteButton && (
                  (() => {
                    if (isUnsavedNewVisit) {
                      return (
                        <SaveButton 
                          type="primary"
                          size="large"
                          loading={saving}
                          icon={<SaveOutlined />}
                          onClick={handleSave}
                          style={{ background:'#1677ff', borderColor:'#1677ff', color:'#fff' }}
                        >
                          Save New Visit
                        </SaveButton>
                      );
                    }
                    if (isVisitDirty) {
                      return (
                        <SaveButton 
                          type="primary"
                          size="large"
                          loading={saving}
                          icon={<SaveOutlined />}
                          onClick={handleSave}
                          style={{ background:'#1677ff', borderColor:'#1677ff', color:'#fff' }}
                        >
                          Update
                        </SaveButton>
                      );
                    }
                    return null;
                  })()
                )}
                {/* Queue mode buttons (Intake page) - Save and Complete */}
                {!recordsMode && activeTab === '1' && !isViewingHistoricalVisit && showCompleteButton && (
                  <>
                    <Button 
                      size="large"
                      loading={saving}
                      icon={<SaveOutlined />}
                      onClick={handleSaveProgress}
                      style={{ height: '52px' }}
                    >
                      Save
                    </Button>
                    <SaveButton 
                      type="primary"
                      size="large"
                      loading={completing}
                      icon={<CheckCircleOutlined />}
                      onClick={handleCompleteScreening}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                    >
                      Complete Screening
                    </SaveButton>
                  </>
                )}
              </Space>
            </FormFooter>
            )}
          </FormCard>
        </Form>
      </Spin>
    </Container>
  );
};

export default PatientEditView;
